// Colour maths mirroring Flutter's `Color`, `HSLColor` and the helpers in
// lib/ui/theme/design_tokens.dart (alphaBlend, lerp, clampedForDarkMode/LightMode).
// Used to derive token values (toast palettes, frosted bases) and by tests that
// pin those values to the spec tables.

export interface Rgba {
  /** 0..255 */
  r: number;
  g: number;
  b: number;
  /** 0..1 */
  a: number;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));

/** Parses `#RGB`, `#RRGGBB`, `#AARRGGBB` (Flutter order when 8 digits), `rgb()` and `rgba()`. */
export function parseColor(input: string): Rgba {
  const value = input.trim();
  if (value.startsWith('#')) {
    const hex = value.slice(1);
    if (hex.length === 3) {
      const [r, g, b] = [...hex].map((c) => parseInt(c + c, 16));
      return { r: r ?? 0, g: g ?? 0, b: b ?? 0, a: 1 };
    }
    if (hex.length === 6) {
      return { r: parseInt(hex.slice(0, 2), 16), g: parseInt(hex.slice(2, 4), 16), b: parseInt(hex.slice(4, 6), 16), a: 1 };
    }
    if (hex.length === 8) {
      return {
        a: parseInt(hex.slice(0, 2), 16) / 255,
        r: parseInt(hex.slice(2, 4), 16),
        g: parseInt(hex.slice(4, 6), 16),
        b: parseInt(hex.slice(6, 8), 16),
      };
    }
  }
  const fn = /^rgba?\(([^)]+)\)$/i.exec(value);
  if (fn?.[1]) {
    const parts = fn[1].split(/[\s,/]+/).filter(Boolean).map(Number);
    const [r = 0, g = 0, b = 0, a = 1] = parts;
    return { r, g, b, a };
  }
  throw new Error(`Unsupported colour: ${input}`);
}

/** `Color.withValues(alpha:)`: replaces the alpha channel. */
export function withAlpha(color: Rgba | string, alpha: number): Rgba {
  const c = typeof color === 'string' ? parseColor(color) : color;
  return { ...c, a: clamp(alpha, 0, 1) };
}

/** `Color.alphaBlend(foreground, background)` (floating-point, as in Flutter 3.27+). */
export function alphaBlend(foreground: Rgba | string, background: Rgba | string): Rgba {
  const fg = typeof foreground === 'string' ? parseColor(foreground) : foreground;
  const bg = typeof background === 'string' ? parseColor(background) : background;
  const alpha = fg.a;
  if (alpha === 0) return bg;
  const inv = 1 - alpha;
  if (bg.a === 1) {
    return { r: alpha * fg.r + inv * bg.r, g: alpha * fg.g + inv * bg.g, b: alpha * fg.b + inv * bg.b, a: 1 };
  }
  const outA = alpha + bg.a * inv;
  if (outA === 0) return { r: 0, g: 0, b: 0, a: 0 };
  const mix = (f: number, b: number) => (f * alpha + b * bg.a * inv) / outA;
  return { r: mix(fg.r, bg.r), g: mix(fg.g, bg.g), b: mix(fg.b, bg.b), a: outA };
}

const hex2 = (v: number) => Math.round(clamp(v, 0, 255)).toString(16).padStart(2, '0').toUpperCase();

/** `#RRGGBB` (alpha ignored). */
export function toHex(color: Rgba): string {
  return `#${hex2(color.r)}${hex2(color.g)}${hex2(color.b)}`;
}

interface Hsl {
  h: number;
  s: number;
  l: number;
  a: number;
}

export function rgbToHsl(color: Rgba): Hsl {
  const r = color.r / 255;
  const g = color.g / 255;
  const b = color.b / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const l = (max + min) / 2;
  let h = 0;
  if (delta !== 0) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  if (h < 0) h += 360;
  const s = l === 1 || l === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  return { h, s: clamp(s, 0, 1), l, a: color.a };
}

export function hslToRgb({ h, s, l, a }: Hsl): Rgba {
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const secondary = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const match = l - chroma / 2;
  let rgb: [number, number, number];
  if (h < 60) rgb = [chroma, secondary, 0];
  else if (h < 120) rgb = [secondary, chroma, 0];
  else if (h < 180) rgb = [0, chroma, secondary];
  else if (h < 240) rgb = [0, secondary, chroma];
  else if (h < 300) rgb = [secondary, 0, chroma];
  else rgb = [chroma, 0, secondary];
  return { r: (rgb[0] + match) * 255, g: (rgb[1] + match) * 255, b: (rgb[2] + match) * 255, a };
}

/** design_tokens.dart `clampedForDarkMode`: raises HSL lightness to at least `minLightness`. */
export function clampedForDarkMode(color: Rgba | string, minLightness = 0.75): Rgba {
  const c = typeof color === 'string' ? parseColor(color) : color;
  const hsl = rgbToHsl(c);
  return hsl.l < minLightness ? hslToRgb({ ...hsl, l: minLightness }) : c;
}
